import type { ReactNode } from 'react';
import { useIsMobile } from '@/hooks/useIsMobile';

/**
 * Responsive "table on desktop, cards on mobile" primitives (mobile UX — ported
 * from the Project Tracker tenant).
 *
 * Oscar list pages are hand-rolled `<table>` blocks wrapped in `overflow-x-auto`.
 * On a phone that means the user swipes sideways through a wide grid and loses
 * row context. These helpers let a page render the SAME data two ways, switched
 * on the viewport at Tailwind's `md` breakpoint (768px):
 *
 *   <DesktopTable> … existing table, moved here verbatim … </DesktopTable>
 *   <MobileCards>{rows.map(r => <MobileCard> … </MobileCard>)}</MobileCards>
 *
 * Only ONE branch mounts at a time (via useIsMobile) rather than both-hidden-by-
 * CSS. That keeps the desktop view byte-for-byte unchanged (at ≥md the mobile
 * branch is simply not rendered), keeps the accessibility tree free of duplicate
 * content, and means jsdom — where matchMedia is absent — always sees the
 * desktop branch, so existing table-based tests are unaffected. The `hidden` /
 * `md:hidden` classes remain as a belt-and-braces CSS guard.
 *
 * These are presentational only — sorting/filtering/mutations stay in the page.
 */

/** Renders the existing desktop table; nothing below the md breakpoint. */
export function DesktopTable({ children, className = '' }: { children: ReactNode; className?: string }) {
  const isMobile = useIsMobile();
  if (isMobile) return null;
  return <div className={'hidden md:block ' + className}>{children}</div>;
}

/** The mobile-only card stack that replaces a wide table below md. */
export function MobileCards({ children, className = '' }: { children: ReactNode; className?: string }) {
  const isMobile = useIsMobile();
  if (!isMobile) return null;
  return <div className={'space-y-2 md:hidden ' + className}>{children}</div>;
}

/**
 * One record rendered as a card. Pass `onClick` to make the whole card a tap
 * target (mirrors clickable table rows). It renders as a `div role="button"`
 * (not a `<button>`) so the card can hold block content and nested links/controls
 * without invalid button nesting; Enter/Space activate it for keyboard users.
 * Without `onClick` it is a plain container and any links inside stay tappable.
 */
export function MobileCard({
  children, onClick, className = '',
}: { children: ReactNode; onClick?: () => void; className?: string }) {
  const base = 'block w-full rounded-lg border border-border bg-white p-3 text-left shadow-sm';
  if (onClick) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
        className={base + ' cursor-pointer hover:bg-bg ' + className}
      >
        {children}
      </div>
    );
  }
  return <div className={base + ' ' + className}>{children}</div>;
}

/**
 * A label/value line inside a MobileCard — the column header becomes the label,
 * the cell becomes the value. Omit `label` (or pass empty) for a full-width
 * value with no leading label (e.g. a title line).
 */
export function CardField({ label, children }: { label?: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-0.5">
      {label
        ? <span className="shrink-0 text-[0.62rem] font-bold uppercase tracking-wider text-darkGrey/45">{label}</span>
        : null}
      <span className="min-w-0 text-sm text-darkGrey">{children}</span>
    </div>
  );
}
